/**
 * AdminContactsEnhanced - Enhanced CRM Contacts list view with standardized table
 * 
 * Features:
 * - Server-side sorting on all columns
 * - Server-side filtering (account, unassigned)
 * - Global search
 * - Multi-select with bulk actions
 * - Admin-only delete with confirmation
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useAdminTable } from '../../hooks/useAdminTable';
import AdminDataTable, {
  DateCell,
  TruncatedCell,
  BulkActionToolbar,
} from '../../components/admin/AdminDataTable';
import AdminListHeader from '../../components/admin/AdminListHeader';
import {
  FilterBar,
  GlobalSearch,
  StatusFilter,
  ActiveFilterChips,
} from '../../components/admin/AdminTableFilters';
import {
  DeleteConfirmationModal,
} from '../../components/admin/BulkActionModals';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { crmAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Eye,
  User,
  Trash2,
  Phone,
  Mail,
  Building2,
  Star,
  Plus,
  AlertTriangle,
  Pencil,
} from 'lucide-react';
import CreateContactModal from '../../components/admin/CreateContactModal';

// ===================================================
// MAIN COMPONENT
// ===================================================

/**
 * AdminContactsEnhanced - Enhanced Contacts list view
 * @param {function} onViewContact - Callback when viewing a contact
 * @param {function} onCreateContact - Callback to open create contact modal
 * @param {string} initialAccountId - Optional account ID to filter contacts by customer
 */
const AdminContactsEnhanced = ({ onViewContact, onCreateContact, initialAccountId = null }) => {
  const { isAdmin } = useAuth();
  
  // Accounts for filtering
  const [accounts, setAccounts] = useState([]);
  
  // Edit modal state
  const [editingContact, setEditingContact] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Bulk action modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Load accounts for filter dropdown
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const data = await crmAPI.getAccounts({ page_size: 200 });
        setAccounts(data.accounts || []);
      } catch (err) {
        console.error('Failed to load accounts:', err);
      }
    };
    loadAccounts();
  }, []);
  
  // Fetch function for contacts - applies initialAccountId
  const fetchContacts = useCallback(async (params) => {
    const adjustedParams = { ...params };
    
    // Apply account filter if provided (from "View All" on Customer page)
    if (initialAccountId) {
      adjustedParams.account_id = initialAccountId;
    }
    
    return await crmAPI.getContacts(adjustedParams);
  }, [initialAccountId]);
  
  // Use admin table hook
  const {
    data: contacts,
    total,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortBy,
    sortDir,
    handleSort,
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectedCount,
    density,
    setDensity,
    refresh,
  } = useAdminTable({
    fetchFn: fetchContacts,
    defaultSortBy: 'created_at',
    defaultSortDir: 'desc',
    defaultPageSize: 25,
    filterKeys: ['account_id', 'unassigned'],
    initialFilters: initialAccountId ? { account_id: initialAccountId } : {},
    transformResponse: (data) => ({
      items: data.contacts || data.items || [],
      total: data.total || 0
    })
  });
  
  // Handle bulk delete
  const handleBulkDelete = async (reason) => {
    setBulkActionLoading(true);
    try {
      const result = await crmAPI.bulkDeleteContacts({
        ids: selectedIds,
        reason
      });
      
      if (result.deleted_count > 0) {
        toast.success(`Deleted ${result.deleted_count} contact(s)`);
      }
      if (result.blocked_items?.length > 0) {
        toast.warning(`${result.blocked_items.length} contact(s) could not be deleted`);
      }
      
      setShowDeleteModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete contacts');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Build account options for filter
  const accountOptions = accounts.map(a => ({
    value: a.id,
    label: a.company_name || a.name || a.id
  }));
  
  // Table columns definition
  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      sortKey: 'first_name',
      render: (_, row) => {
        const fullName = [row.first_name, row.last_name].filter(Boolean).join(' ') || '—';
        return (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-ivory-100 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-ivory-500" />
            </div>
            <div className="min-w-0">
              <button
                onClick={() => onViewContact?.(row.id)}
                className="font-medium text-ivory-800 hover:text-blue-600 hover:underline flex items-center gap-1"
              >
                {fullName}
                {row.is_primary && (
                  <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                )}
              </button>
              {row.title && (
                <p className="text-xs text-ivory-500 truncate max-w-[180px]">{row.title}</p>
              )}
            </div>
          </div>
        );
      }
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      render: (value) => (
        value ? (
          <a 
            href={`mailto:${value}`}
            className="flex items-center gap-1.5 text-blue-600 hover:underline text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <Mail className="w-3.5 h-3.5" />
            <TruncatedCell value={value} maxWidth={180} />
          </a>
        ) : (
          <span className="text-ivory-400">—</span>
        )
      )
    },
    {
      key: 'phone',
      header: 'Phone',
      sortable: false,
      render: (value) => (
        value ? (
          <div className="flex items-center gap-1.5 text-ivory-600">
            <Phone className="w-3.5 h-3.5" />
            <span className="text-sm">{value}</span>
          </div>
        ) : (
          <span className="text-ivory-400">—</span>
        )
      )
    },
    {
      key: 'account_name',
      header: 'Company',
      sortable: false,
      render: (value, row) => (
        value || row.account_id ? (
          <div className="flex items-center gap-1.5 text-ivory-600">
            <Building2 className="w-3.5 h-3.5" />
            <span className="text-sm truncate max-w-[150px]">{value || 'Linked'}</span>
          </div>
        ) : (
          <Badge variant="outline" className="text-xs text-orange-600 border-orange-300 bg-orange-50">
            Unassigned
          </Badge>
        )
      )
    },
    {
      key: 'preferred_contact_method',
      header: 'Preferred',
      sortable: false,
      render: (value) => (
        value ? (
          <Badge variant="outline" className="text-xs capitalize">
            {value}
          </Badge>
        ) : (
          <span className="text-ivory-400">—</span>
        )
      )
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (value) => <DateCell value={value} />
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setEditingContact(row);
            }}
            title="Edit Contact"
            data-testid={`edit-contact-${row.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewContact?.(row.id);
            }}
            title="View Details"
            data-testid={`view-contact-${row.id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];
  
  // Filter labels for chips
  const filterLabels = {
    account_id: 'Company',
    unassigned: 'Unassigned',
  };
  
  // Format filter values for display
  const formatFilterValue = (key, value) => {
    if (key === 'account_id') {
      const account = accounts.find(a => a.id === value);
      return account?.company_name || account?.name || value;
    }
    if (key === 'unassigned') {
      return value === 'true' ? 'Yes' : 'No';
    }
    return value;
  };
  
  return (
    <div className="space-y-6" data-testid="admin-contacts-enhanced">
      {/* Header */}
      <AdminListHeader
        title="Contacts"
        subtitle={total > 0 ? `${total.toLocaleString()} total contacts` : undefined}
        createLabel="Add Contact"
        onCreate={() => setShowCreateModal(true)}
        canCreate={isAdmin}
        onRefresh={refresh}
        refreshing={loading}
        density={density}
        onDensityChange={setDensity}
      />
      
      {/* Filters */}
      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearFilters}>
        <GlobalSearch
          value={search}
          onChange={setSearch}
          placeholder="Search name, email, phone..."
          className="flex-1 min-w-[250px]"
        />
        
        <StatusFilter
          value={filters.account_id}
          onChange={(v) => setFilter('account_id', v)}
          options={accountOptions}
          placeholder="All Companies"
          className="w-[180px]"
          data-testid="contact-account-filter"
        />
        
        <StatusFilter
          value={filters.unassigned}
          onChange={(v) => setFilter('unassigned', v)}
          options={[
            { value: 'true', label: 'Unassigned Only' }
          ]}
          placeholder="Assignment"
          className="w-[160px]"
          data-testid="contact-unassigned-filter"
        />
      </FilterBar>
      
      {/* Active filter chips */}
      <ActiveFilterChips
        filters={filters}
        filterLabels={filterLabels}
        onRemove={(key) => setFilter(key, null)}
        onClearAll={clearFilters}
        formatValue={formatFilterValue}
      />
      
      {/* Bulk action toolbar */}
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
      >
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        )}
      </BulkActionToolbar>
      
      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      {/* Data Table */}
      <AdminDataTable
        tableId="contacts"
        data={contacts}
        columns={columns}
        loading={loading}
        emptyMessage="No contacts found"
        emptyIcon={User}
        
        // Selection
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(row) => row.id}
        
        // Sorting
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        
        // Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        
        // Density
        density={density}
        onDensityChange={setDensity}
        
        // Column preferences
        showColumnSelector={true}
        resizableColumns={true}
        
        // Row click opens detail
        onRowClick={(row) => onViewContact?.(row.id)}
        
        data-testid="contacts-data-table"
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        count={selectedCount}
        itemType="contact"
        loading={bulkActionLoading}
        warnings={[
          'Soft-deleted contacts can be restored by an administrator.',
        ]}
        requireReason={true}
      />
      
      {/* Create Contact Modal */}
      <CreateContactModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          refresh();
        }}
        prefilledAccountId={initialAccountId}
        accounts={accounts}
      />
      
      {/* Edit Contact Modal */}
      <CreateContactModal
        isOpen={!!editingContact}
        onClose={() => setEditingContact(null)}
        onSuccess={() => {
          setEditingContact(null);
          refresh();
        }}
        editContact={editingContact}
        accounts={accounts}
      />
    </div>
  );
};

export default AdminContactsEnhanced;
